import { Input, Race, VCLASSES, physOf } from "./physics";
import { SEASON, TRACKS, TrackDef } from "./track";

/** Input bitmask per video frame: accel 1, brake 2, jet 4, left 8, right 16. */
export function maskOf(i: Input): number {
  return (i.accel ? 1 : 0) | (i.brake ? 2 : 0) | (i.jet ? 4 : 0) | (i.left ? 8 : 0) | (i.right ? 16 : 0);
}
export function inputOf(m: number): Input {
  return { accel: !!(m & 1), brake: !!(m & 2), jet: !!(m & 4), left: !!(m & 8), right: !!(m & 16) };
}

/** Run-length encode a per-frame mask log into [mask, count] pairs. */
export function rle(log: number[]): [number, number][] {
  const out: [number, number][] = [];
  for (const m of log) {
    const last = out[out.length - 1];
    if (last && last[0] === m) last[1]++;
    else out.push([m, 1]);
  }
  return out;
}

/** UTC calendar day as YYYYMMDD. */
export const utcDay = (t = Date.now()) => new Date(t).toISOString().slice(0, 10).replace(/-/g, "");
export function dayHash(day: string): number {
  let h = 2166136261;
  for (const ch of day + "F1") h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  h ^= h >>> 15;
  h = Math.imul(h, 2246822519);
  return (h ^ (h >>> 13)) >>> 0;
}
export const DAILY_SKILL = 2; // EXPERT
/** Everything about the daily challenge follows from the date, so every player gets the same race. */
export function dailyOf(day: string, pool: number[] = SEASON) {
  const h = dayHash(day);
  return { day, track: pool[h % pool.length], seed: (h ^ 0x9e3779b9) >>> 0, skill: DAILY_SKILL, vclass: 0 };
}

export interface RunSubmission {
  name: string;
  key?: string; // the pilot code: the server files the run under the name registered for it
  track: number; // index into TRACKS
  machine: number;
  seed: number;
  ai: number;
  skill?: number; // CPU level, default PRO
  vclass?: number; // vehicle category: only daily challenges may use another one than the formula car (0)
  mode?: "race" | "tt" | "daily" | "weekly"; // race (default), time trial (no CPU, seed 0), daily or weekly challenge
  qual?: string; // time trial of a tournament qualifier (the id says which one; the server checks that it is open)
  circuit?: string; // time trial on a circuit shared by a player (track is then SHARED_ID, the code says which one)
  day?: string; // daily challenge date, or Monday of the weekly challenge, YYYYMMDD
  phys?: number; // physics revision the run was driven with (absent: 0, the original); the server replays it with that one
  rle: [number, number][];
  dev?: number; // what the player drove with, as the game declares it: 0 keyboard, 1 touch, 2 gamepad (shown beside the time, never checked)
}

export const RANKED_AI = 9; // only full-grid races are ranked
export const MIN_RANKED_SKILL = 1; // ROOKIE races are for practice and not ranked
export const MAX_FRAMES = 60 * 60 * 20;
export const MAX_RLE = 6000;

export type Verdict =
  | { ok: true; finishFrame: number; lapFrames: number[] }
  | { ok: false; error: string };

/**
 * Re-simulates the whole race from the seed and the recorded inputs with the shared deterministic
 * model. The server only trusts the times it computes itself.
 */
export type TrackLookup = (i: number) => TrackDef | undefined;
/** `trackOf` lets a worker thread (which has only the circuit it was sent) replay a run without the server's TRACKS table. */
export function verifyRun(r: RunSubmission, daily: (day: string) => { track: number; seed: number; skill: number; vclass?: number; ai?: number; nojet?: boolean; machine?: number } = dailyOf, trackOf: TrackLookup = (i) => TRACKS[i]): Verdict {
  const isInt = (n: unknown): n is number => typeof n === "number" && Number.isInteger(n);
  const track = isInt(r.track) && r.track >= 0 ? trackOf(r.track) : undefined;
  if (!track) return { ok: false, error: "bad track" };
  if (r.machine !== 0 && r.machine !== 1) return { ok: false, error: "bad machine" };
  if (!isInt(r.seed) || r.seed < 0 || r.seed > 0xffffffff) return { ok: false, error: "bad seed" };
  const mode = r.mode ?? "race";
  const vclass = r.vclass ?? 0;
  if (!isInt(vclass) || vclass < 0 || vclass >= VCLASSES.length) return { ok: false, error: "bad vehicle" };
  let skill = r.skill ?? 1;
  let nojet = false;
  if (!isInt(skill) || skill < 0 || skill > 3) return { ok: false, error: "bad skill" };
  if (mode === "tt") {
    if (r.ai !== 0 || r.seed !== 0) return { ok: false, error: "bad time trial" };
    skill = 1;
  } else if (mode === "daily" || mode === "weekly") {
    if (typeof r.day !== "string" || !/^\d{8}$/.test(r.day)) return { ok: false, error: "bad day" };
    const d = daily(r.day); // for a weekly run: the week's configuration (its rule included)
    if (r.track !== d.track || r.seed !== d.seed || r.ai !== (d.ai ?? RANKED_AI) || vclass !== (d.vclass ?? 0)) return { ok: false, error: "bad " + mode };
    if (d.machine !== undefined && d.machine >= 0 && r.machine !== d.machine) return { ok: false, error: "bad " + mode };
    nojet = !!d.nojet;
    skill = d.skill;
  } else if (mode !== "race") return { ok: false, error: "bad mode" };
  else if (r.ai !== RANKED_AI || skill < MIN_RANKED_SKILL) return { ok: false, error: "unranked" };
  if (mode !== "daily" && mode !== "weekly" && vclass !== 0) return { ok: false, error: "bad vehicle" };
  if (!Array.isArray(r.rle) || r.rle.length === 0 || r.rle.length > MAX_RLE) return { ok: false, error: "bad inputs" };
  let total = 0;
  for (const p of r.rle) {
    if (!Array.isArray(p) || !isInt(p[0]) || !isInt(p[1]) || p[0] < 0 || p[0] > 31 || p[1] < 1) return { ok: false, error: "bad inputs" };
    if (nojet && p[0] & 4) return { ok: false, error: "turbo not allowed" };
    total += p[1];
    if (total > MAX_FRAMES) return { ok: false, error: "too long" };
  }
  const race = new Race({ track, machines: [r.machine], aiCount: r.ai, seed: r.seed, skill, vclass, phys: physOf(r.phys) });
  const me = race.cars[0];
  for (const [mask, count] of r.rle) {
    const input = inputOf(mask);
    for (let k = 0; k < count; k++) {
      race.step([input]);
      if (me.finished) return { ok: true, finishFrame: me.finishFrame, lapFrames: [...me.lapFrames] };
    }
  }
  return { ok: false, error: "race not finished" };
}
